import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { IconButton, CircularProgress } from "@mui/material";
import { Done as DoneIcon, Close as CloseIcon } from "@mui/icons-material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import "./documents.css";
import DualBall from "../../../../utilities/dual ball back.svg";
import { toPktDateStr } from "../../../../utils/leaveDays";

const DOC_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const ordinalDay = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
// e.g. "5th January 2026" (in PKT, consistent with the rest of the app).
const formatDocDate = (value) => {
  const ymd = toPktDateStr(value);
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return `${ordinalDay(d)} ${DOC_MONTHS[m - 1]} ${y}`;
};

const Documents = (props) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [officialDocuments, setOfficialDocuments] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState(() => new Set());
  const [preview, setPreview] = useState(null); // { doc, url }

  const [loader, showLoader] = useState(false);
  const [showMyDocs, setShowMyDocs] = useState(true);

  const Employee = props.Employee;
  const user = props.user;

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/documents?email=${Employee.email}`
      );
      const data = await response.json();

      if (Array.isArray(data)) {
        const officialDocs = data.filter((doc) => doc.email === "hr@growthguild.us");
        const personalDocs = data.filter((doc) => doc.email !== "hr@growthguild.us");

        const filteredDocuments = personalDocs.filter(
          (doc) => doc.status === "Pending" || doc.status === "Accepted"
        );

        const sortedDocuments = filteredDocuments.sort((a, b) => {
          if (a.status === "Pending" && b.status === "Accepted") return -1;
          if (a.status === "Accepted" && b.status === "Pending") return 1;
          return new Date(b.uploadTime) - new Date(a.uploadTime); // Latest first
        });

        setDocuments(sortedDocuments);
        setOfficialDocuments(officialDocs);
      } else {
        console.error("Unexpected response format:", data);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [Employee]);

  // While the preview is open: Escape closes it and the background can't scroll.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        window.URL.revokeObjectURL(preview.url);
        setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [preview]);

  const onFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const onFileUpload = async () => {
    showLoader(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", Employee.name);
    formData.append("email", Employee.email);

    try {
      const response = await fetch("https://hr-backend-72v1.onrender.com/upload", {
        method: "POST",
        body: formData,
      });

      await response.json();
      showLoader(false);

      fetchDocuments();
    } catch (error) {
      console.error("Error:");
      showLoader(false);
    }
  };

  const handleApprove = async (documentId) => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/documents/${documentId}/approve`,
        { method: "POST" }
      );
      if (response.ok) {
        fetchDocuments();
      }
    } catch (error) {
      console.error("Error approving document:");
    }
  };

  const handleReject = async (documentId) => {
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/documents/${documentId}/reject`,
        { method: "POST" }
      );
      if (response.ok) {
        fetchDocuments();
      }
    } catch (error) {
      console.error("Error rejecting document:");
    }
  };

  const handleToggle = (tab) => {
    setShowMyDocs(tab === "my");
  };

  // Open a live preview inside HRMS. We fetch the file by _id (the /documents list
  // omits fileId), turn it into a blob object-URL, and render it in an in-app modal
  // (PDFs/images render inline). A Download button in the modal still lets them save it.
  const handleOpen = async (doc) => {
    if (downloadingIds.has(doc._id)) return; // already opening - ignore repeat clicks
    setDownloadingIds((prev) => new Set(prev).add(doc._id));
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/documents/${doc._id}/download`
      );
      if (!response.ok) throw new Error("Preview failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreview({ doc, url });
    } catch (error) {
      console.error("Error opening document:", error);
      alert("Could not open the file. Please try again.");
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(doc._id);
        return next;
      });
    }
  };

  const closePreview = () => {
    setPreview((p) => {
      if (p?.url) window.URL.revokeObjectURL(p.url);
      return null;
    });
  };

  const isHrOrExec = user.role === "HR" || user.role === "Executive";

  const StatusPill = ({ status }) => (
    <span
      className={`docPill ${status === "Accepted" ? "docPill--ok" : "docPill--pending"}`}
    >
      {status}
    </span>
  );

  // Shared document list/table renderer.
  const DocList = ({ docs, withActions, emptyIcon, emptyTitle, emptySub }) => {
    const cols = withActions ? "1fr 180px 130px 110px" : "1fr 180px 130px";
    return (
      <section className="docCard">
        <div className="docRow docRow--head" style={{ gridTemplateColumns: cols }}>
          <span>Name</span>
          <span>Date</span>
          <span>Status</span>
          {withActions && <span>Actions</span>}
        </div>
        {docs.length > 0 ? (
          docs.map((doc) => {
            const isDownloading = downloadingIds.has(doc._id);
            return (
            <div className="docRow" key={doc._id} style={{ gridTemplateColumns: cols }}>
              <button
                className="docNameLink"
                onClick={() => handleOpen(doc)}
                disabled={isDownloading}
                title={isDownloading ? "Opening…" : `Preview ${doc.fileName}`}
              >
                {isDownloading ? (
                  <CircularProgress size={15} thickness={5} sx={{ color: "#e6c469" }} />
                ) : (
                  <DescriptionOutlinedIcon sx={{ fontSize: 17 }} />
                )}
                <span className="docNameText">{doc.fileName}</span>
                {isDownloading && (
                  <span className="docDownloading">opening…</span>
                )}
              </button>
              <span className="docDate">
                {formatDocDate(doc.uploadTime)}
              </span>
              <span>
                <StatusPill status={doc.status} />
              </span>
              {withActions && (
                <span>
                  {doc.status === "Pending" && (
                    <span className="docActions">
                      <IconButton color="success" size="small" onClick={() => handleApprove(doc._id)}>
                        <DoneIcon />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleReject(doc._id)}>
                        <CloseIcon />
                      </IconButton>
                    </span>
                  )}
                </span>
              )}
            </div>
            );
          })
        ) : (
          <div className="docEmpty">
            <span className="docEmptyIcon">{emptyIcon}</span>
            <div>
              <div className="docEmptyTitle">{emptyTitle}</div>
              <div className="docEmptySub">{emptySub}</div>
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="docWrap">
      <header className="docHeader">
        <div>
          <h1 className="docTitle">Documents</h1>
          <p className="docSubtitle">Your uploads and official Growth Guild files</p>
        </div>
        <div className="docSeg">
          <button
            className={`docSegBtn ${showMyDocs ? "docSegBtn--active" : ""}`}
            onClick={() => handleToggle("my")}
          >
            My Docs
          </button>
          <button
            className={`docSegBtn ${!showMyDocs ? "docSegBtn--active" : ""}`}
            onClick={() => handleToggle("official")}
          >
            GG Official Docs
          </button>
        </div>
      </header>

      {showMyDocs ? (
        <>
          <div className="docUpload">
            <span className="docUploadIcon">
              <FileUploadOutlinedIcon sx={{ fontSize: 22 }} />
            </span>
            <div className="docUploadText">
              <div className="docUploadTitle">Upload a document</div>
              <div className="docUploadSub">
                Browse a file - your manager will review it
              </div>
            </div>
            <label className="docChooseBtn">
              <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
              Choose File
              <input type="file" onChange={onFileChange} disabled={loader} hidden />
            </label>
            <span className="docFileName">
              {selectedFile ? selectedFile.name : "No file chosen"}
            </span>
            <button
              className="docUploadBtn"
              onClick={onFileUpload}
              disabled={loader || !selectedFile}
            >
              Upload!
            </button>
            {loader && (
              <img className="dual-ball" src={DualBall} alt="Uploading" width="46" height="46" />
            )}
          </div>

          <DocList
            docs={documents}
            withActions={isHrOrExec}
            emptyIcon={<LightbulbOutlinedIcon sx={{ fontSize: 26 }} />}
            emptyTitle="Every list starts with the first item"
            emptySub="Upload a document above to get started."
          />
        </>
      ) : (
        <DocList
          docs={officialDocuments}
          withActions={false}
          emptyIcon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 26 }} />}
          emptyTitle="No official documents yet"
          emptySub="Growth Guild official files will appear here."
        />
      )}

      {preview &&
        createPortal(
          <div
            className="docPreviewOverlay"
            onClick={closePreview}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 100000,
              background: "rgba(0,0,0,0.78)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Transparent close button, top-right of the screen */}
            <button
              className="docPreviewClose"
              onClick={(e) => {
                e.stopPropagation();
                closePreview();
              }}
              aria-label="Close preview"
              style={{
                position: "fixed",
                top: "22px",
                right: "30px",
                width: "46px",
                height: "46px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.28)",
                background: "rgba(255,255,255,0.10)",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100001,
              }}
            >
              <CloseIcon />
            </button>

            {/* The document: 80% x 80%, centered. Clicking outside it closes. */}
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "80vw",
                height: "80vh",
                background: "#fff",
                borderRadius: "12px",
                overflow: "hidden",
                boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
              }}
            >
              <iframe
                title={preview.doc.fileName}
                src={preview.url}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Documents;
